import React,{useEffect,useMemo,useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {motion,AnimatePresence} from 'framer-motion';
import {useSupabaseData} from '../contexts/SupabaseDataContext';
import {useSupabaseAuth} from '../contexts/SupabaseAuthContext';
import {format} from 'date-fns';
import {getOrdersForSeller} from '../services/sellerIdentityService';
import {filterAndSortOrders} from '../services/orderQueryService';
import {
  getHideDeclinedOrders,
  setHideDeclinedOrders
} from '../services/orderVisibilityPreferences';
import useUserPreference from '../hooks/useUserPreference';
import SafeIcon from '../common/SafeIcon';
import SellerOrderControls from '../components/dashboard/SellerOrderControls';
import SellerOrderPagination from '../components/dashboard/SellerOrderPagination';
import SellerOrdersCalendar from '../components/dashboard/SellerOrdersCalendar';
import SellerProfileTab from '../components/dashboard/SellerProfileTab';
import SellerMembershipPlanTab from '../components/dashboard/SellerMembershipPlanTab';
import OfferingsTab from '../components/dashboard/OfferingsTab';
import PolicyBuilderWizard from '../components/dashboard/PolicyBuilderWizard';
import InvoiceDocument from '../components/orders/InvoiceDocument';
import PickupConfirmationPanel from '../components/orders/PickupConfirmationPanel';
import DeliveryConfirmationButton from '../components/orders/DeliveryConfirmationButton';
import OrderCustomizationSummary from '../components/orders/OrderCustomizationSummary';
import OrderPriceBreakdown from '../components/orders/OrderPriceBreakdown';
import PaymentsTab from '../components/dashboard/PaymentsTab';
import SellerFinanceTab from '../components/dashboard/SellerFinanceTab';
import SellerTaxTab from '../components/dashboard/SellerTaxTab';
import * as FiIcons from 'react-icons/fi';

const {
  FiShoppingBag,
  FiUsers,
  FiCheck,
  FiClock,
  FiPlus,
  FiPackage,
  FiMessageCircle,
  FiChevronDown,
  FiDollarSign,
  FiShield,
  FiBarChart2,
  FiPercent,
  FiLoader,
  FiCalendar,
  FiCreditCard
}=FiIcons;

const formatPrice=(price)=>{
  if (!price) return '$0.00';
  const value=typeof price==='string' ? price.replace('$','') : price;
  return `$${parseFloat(value).toFixed(2)}`;
};

const SellerDashboardPage=()=>{
  const navigate=useNavigate();
  const {user}=useSupabaseAuth();
  const {orders,updateOrderStatus,sellers}=useSupabaseData();
  const [activeTab,setActiveTab]=useUserPreference(
    user?.id,
    'seller-hub-tab',
    'orders'
  );
  const [expandedOrderId,setExpandedOrderId]=useUserPreference(
    user?.id,
    'seller-expanded-order',
    null
  );
  const [orderToPrint,setOrderToPrint]=useState(null);
  const [orderSearch,setOrderSearch]=useUserPreference(
    user?.id,
    'seller-order-search',
    ''
  );
  const [orderFilter,setOrderFilter]=useUserPreference(
    user?.id,
    'seller-order-filter',
    'all'
  );
  const [orderSort,setOrderSort]=useUserPreference(
    user?.id,
    'seller-order-sort',
    'newest'
  );
  const [hideDeclined,setHideDeclined]=useState(
    ()=>getHideDeclinedOrders(user?.id)
  );
  const [updatingOrderId,setUpdatingOrderId]=useState(null);
  const [updatingStatus,setUpdatingStatus]=useState('');

  const currentSeller=sellers.find((seller)=>seller.user_id===user?.id);
  const normalizedTaxNumber=String(
    currentSeller?.gst_hst_registration_number || ''
  ).replace(/[^0-9]/g,'');
  const hasRegisteredTax= currentSeller?.tax_registration_status==='registered'
    && /^[0-9]{9}$/.test(normalizedTaxNumber);

  useEffect(()=>{
    const nextValue=getHideDeclinedOrders(user?.id);
    setHideDeclined(nextValue);

    const handlePreferenceChange=()=>{
      setHideDeclined(getHideDeclinedOrders(user?.id));
    };

    window.addEventListener(
      'sohlocal:declined-order-visibility-changed',
      handlePreferenceChange
    );

    return ()=>{
      window.removeEventListener(
        'sohlocal:declined-order-visibility-changed',
        handlePreferenceChange
      );
    };
  },[user?.id]);

  useEffect(()=>{
    if (!hasRegisteredTax && activeTab==='tax') {
      setActiveTab('orders');
    }
  },[hasRegisteredTax,activeTab,setActiveTab]);

  const sellerOrders=getOrdersForSeller(orders,currentSeller,sellers);
  const filteredSellerOrders=useMemo(
    ()=>filterAndSortOrders(
      sellerOrders,
      {
        search:orderSearch,
        status:orderFilter,
        sort:orderSort,
        role:'selling',
        userId:user?.id,
        sellerId:currentSeller?.id,
        hideDeclined
      }
    ),
    [
      sellerOrders,
      orderSearch,
      orderFilter,
      orderSort,
      user?.id,
      currentSeller?.id,
      hideDeclined
    ]
  );

  const isProfileComplete=!!currentSeller?.business_name
    && !!currentSeller?.description
    && currentSeller.description.length>20
    && !!currentSeller?.location
    && (
      currentSeller?.profile_images?.length>0
      || !!currentSeller?.profile_image
    );

  const stats=[
    {
      name:'Total Orders',
      value:sellerOrders.length,
      icon:FiShoppingBag,
      color:'text-blue-600 bg-blue-50'
    },
    {
      name:'Pending',
      value:sellerOrders.filter((order)=>order.status==='pending').length,
      icon:FiClock,
      color:'text-yellow-600 bg-yellow-50'
    },
    {
      name:'Active',
      value:sellerOrders.filter((order)=>[
        'accepted',
        'preparing',
        'ready'
      ].includes(order.status)).length,
      icon:FiPackage,
      color:'text-emerald-600 bg-emerald-50'
    },
    {
      name:'Completed',
      value:sellerOrders.filter((order)=>order.status==='completed').length,
      icon:FiCheck,
      color:'text-green-600 bg-green-50'
    }
  ];

  const handleStatusUpdate=async(event,orderId,status)=>{
    event.stopPropagation();

    if (updatingOrderId) return;

    setUpdatingOrderId(orderId);
    setUpdatingStatus(status);

    try {
      await updateOrderStatus(orderId,status);
    } catch (error) {
      window.alert(error.message || 'Unable to update this order.');
    } finally {
      setUpdatingOrderId(null);
      setUpdatingStatus('');
    }
  };

  const toggleExpand=(orderId)=>{
    setExpandedOrderId(
      expandedOrderId===orderId ? null : orderId
    );
  };

  const handleCalendarOrderSelect=(order)=>{
    setOrderSearch('');
    setOrderFilter('all');
    if (order.status==='declined' && hideDeclined) {
      setHideDeclined(false);
      setHideDeclinedOrders(user?.id,false);
    }
    setExpandedOrderId(order.id);
    setActiveTab('orders');
  };

  const getStatusBadge=(status)=>{
    const styles={
      pending:'bg-yellow-100 text-yellow-800',
      accepted:'bg-blue-100 text-blue-800',
      preparing:'bg-purple-100 text-purple-800',
      ready:'bg-emerald-100 text-emerald-800',
      completed:'bg-green-100 text-green-800',
      declined:'bg-red-100 text-red-800',
      cancelled:'bg-red-100 text-red-800'
    };

    return styles[status] || 'bg-gray-100 text-gray-800';
  };

  return(
    <div className="min-h-[calc(100vh-64px)] bg-gray-50 py-8">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-8 flex flex-col justify-between gap-6 md:flex-row md:items-center">
          <div>
            <h1 className="mb-1 text-4xl font-black tracking-tight text-gray-900">
              Seller <span className="text-indigo-600">Hub</span>
            </h1>
            <p className="text-sm font-black uppercase tracking-widest text-gray-400">
              {currentSeller?.business_name || 'Manage your business'}
            </p>
          </div>

          <button
            onClick={()=>setActiveTab('offerings')}
            disabled={!isProfileComplete}
            className="rounded-2xl bg-indigo-600 px-8 py-4 text-sm font-black uppercase tracking-[0.2em] text-white disabled:opacity-50"
          >
            <SafeIcon icon={FiPlus} className="mr-2 inline" />
            New Listing
          </button>
        </div>

        {currentSeller && (
          <div className="mb-8 grid grid-cols-2 gap-6 lg:grid-cols-4">
            {stats.map((stat)=>(
              <div
                key={stat.name}
                className="rounded-[2rem] border border-gray-100 bg-white p-8 shadow-sm"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="mb-1 text-xs font-black uppercase tracking-widest text-gray-400">
                      {stat.name}
                    </p>
                    <p className="text-3xl font-black text-gray-900">
                      {stat.value}
                    </p>
                  </div>
                  <div className={`flex h-14 w-14 items-center justify-center rounded-2xl ${stat.color}`}>
                    <SafeIcon icon={stat.icon} className="text-2xl" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="overflow-hidden rounded-[2.5rem] border border-gray-100 bg-white shadow-sm">
          <div className="border-b border-gray-100">
            <nav className="no-scrollbar flex space-x-12 overflow-x-auto px-8">
              {[
                {id:'orders',name:'Orders',icon:FiShoppingBag},
                {id:'calendar',name:'Calendar',icon:FiCalendar},
                {id:'profile',name:'Profile',icon:FiUsers},
                {id:'plans',name:'Plans',icon:FiCreditCard},
                {id:'offerings',name:'Menu Items',icon:FiPlus},
                {id:'payments',name:'Payments',icon:FiDollarSign},
                {id:'finance',name:'Finance',icon:FiBarChart2},
                ...(hasRegisteredTax
                  ? [{id:'tax',name:'Tax',icon:FiPercent}]
                  : []),
                {id:'policies',name:'Policies',icon:FiShield}
              ].map((tab)=>(
                <button
                  key={tab.id}
                  onClick={()=>setActiveTab(tab.id)}
                  className={`relative whitespace-nowrap border-b-4 px-1 py-8 text-sm font-black uppercase tracking-widest ${activeTab===tab.id ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-400'}`}
                >
                  <SafeIcon icon={tab.icon} className="mr-3 inline text-lg" />
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-10">
            {activeTab==='orders' && (
              <div className="space-y-6">
                <SellerOrderControls
                  search={orderSearch}
                  setSearch={setOrderSearch}
                  filter={orderFilter}
                  setFilter={setOrderFilter}
                  sort={orderSort}
                  setSort={setOrderSort}
                  userId={user?.id}
                  hideDeclined={hideDeclined}
                  setHideDeclined={(nextValue)=>{
                    setHideDeclined(nextValue);
                    setHideDeclinedOrders(user?.id,nextValue);
                  }}
                />

                {filteredSellerOrders.length>0 ? (
                  <SellerOrderPagination>
                    {filteredSellerOrders.map((order)=>{
                      const isExpanded=expandedOrderId===order.id;

                      return(
                        <div
                          key={order.id}
                          className={`overflow-hidden rounded-[2.5rem] border bg-white shadow-sm ${isExpanded ? 'border-indigo-200 ring-1 ring-indigo-50' : 'border-gray-100'}`}
                        >
                          <div
                            onClick={()=>toggleExpand(order.id)}
                            className="flex cursor-pointer flex-col items-center justify-between gap-6 p-8 md:flex-row"
                          >
                            <div className="flex flex-wrap items-center gap-6">
                              <div className={`flex h-14 w-14 items-center justify-center rounded-2xl ${isExpanded ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-400'}`}>
                                <SafeIcon
                                  icon={isExpanded ? FiPackage : FiShoppingBag}
                                  className="text-2xl"
                                />
                              </div>

                              <div>
                                <div className="mb-1 flex flex-wrap items-center gap-3">
                                  <span className={`rounded-full px-3 py-1 text-[10px] font-black uppercase tracking-widest ${getStatusBadge(order.status)}`}>
                                    {order.status}
                                  </span>
                                  <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400">
                                    {format(new Date(order.created_at),'MMM d,h:mm a')}
                                  </span>
                                </div>

                                <h3 className="text-xl font-black tracking-tight text-slate-900">
                                  {order.buyer?.name || 'Anonymous User'}
                                </h3>
                              </div>
                            </div>

                            <div className="flex items-center gap-10">
                              <span className="text-3xl font-black text-indigo-600">
                                {formatPrice(order.total_amount)}
                              </span>
                              <SafeIcon
                                icon={FiChevronDown}
                                className={`text-xl text-slate-400 ${isExpanded ? 'rotate-180' : ''}`}
                              />
                            </div>
                          </div>

                          <AnimatePresence>
                            {isExpanded && (
                              <motion.div
                                initial={{height:0,opacity:0}}
                                animate={{height:'auto',opacity:1}}
                                exit={{height:0,opacity:0}}
                              >
                                <div className="border-t border-slate-50 bg-slate-50/30 p-10">
                                  <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                                    <div className="rounded-2xl border border-slate-100 bg-white p-6">
                                      <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                                        Requested Date
                                      </p>
                                      <p className="mt-2 text-lg font-black text-slate-900">
                                        {order.requested_date
                                          ? format(new Date(order.requested_date),'EEEE,MMM do')
                                          : 'TBD'}
                                      </p>
                                    </div>

                                    <div className="rounded-2xl border border-slate-100 bg-white p-6">
                                      <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                                        Delivery Method
                                      </p>
                                      <p className="mt-2 text-lg font-black capitalize text-slate-900">
                                        {order.delivery_method || 'Pickup'}
                                      </p>
                                    </div>
                                  </div>

                                  <div className="mt-8 space-y-4">
                                    {order.items?.map((item,index)=>(
                                      <div
                                        key={index}
                                        className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm"
                                      >
                                        <div className="flex items-center">
                                          <div className="mr-5 h-16 w-16 overflow-hidden rounded-xl bg-gray-50">
                                            <img
                                              src={item.image}
                                              className="h-full w-full object-cover"
                                              alt=""
                                            />
                                          </div>
                                          <div>
                                            <p className="text-lg font-black text-gray-900">
                                              {item.title}
                                            </p>
                                            <p className="text-sm font-black uppercase tracking-widest text-indigo-600">
                                              Qty: {item.quantity} · {formatPrice(item.price)} each
                                            </p>
                                          </div>
                                        </div>

                                        <OrderPriceBreakdown
                                          item={item}
                                          currency={order.stripe_currency || 'cad'}
                                        />
                                        <OrderCustomizationSummary item={item} />
                                      </div>
                                    ))}
                                  </div>

                                  <div className="mt-8 rounded-2xl border border-slate-100 bg-white p-6">
                                    <div className="flex items-center justify-between">
                                      <span className="text-xs font-black uppercase tracking-widest text-slate-900">
                                        Total Transaction
                                      </span>
                                      <span className="text-2xl font-black text-indigo-600">
                                        {formatPrice(order.total_amount)}
                                      </span>
                                    </div>
                                  </div>

                                  {order.status==='ready'
                                    && order.delivery_method==='pickup'
                                    && (
                                      <PickupConfirmationPanel
                                        order={order}
                                        role="seller"
                                        onConfirmed={()=>setExpandedOrderId(null)}
                                      />
                                    )}

                                  {order.status==='ready'
                                    && order.delivery_method==='delivery'
                                    && (
                                      <div className="mt-8 flex justify-end">
                                        <DeliveryConfirmationButton
                                          order={order}
                                          onConfirmed={()=>setExpandedOrderId(null)}
                                        />
                                      </div>
                                    )}

                                  {updatingOrderId===order.id && (
                                    <div className="mt-8 flex items-center gap-3 rounded-2xl border border-indigo-100 bg-indigo-50 px-5 py-4 text-sm font-black text-indigo-700">
                                      <SafeIcon
                                        icon={FiLoader}
                                        className="text-lg animate-spin"
                                      />
                                      Updating order to {updatingStatus}...
                                    </div>
                                  )}

                                  <div className="mt-8 flex flex-wrap gap-4">
                                    {order.status==='pending' && (
                                      <>
                                        <button
                                          onClick={(event)=>handleStatusUpdate(event,order.id,'accepted')}
                                          disabled={!!updatingOrderId}
                                          aria-busy={updatingOrderId===order.id && updatingStatus==='accepted'}
                                          className="rounded-2xl bg-indigo-600 px-8 py-4 text-xs font-black uppercase tracking-widest text-white"
                                        >
                                          Accept
                                        </button>
                                        <button
                                          onClick={(event)=>handleStatusUpdate(event,order.id,'declined')}
                                          disabled={!!updatingOrderId}
                                          aria-busy={updatingOrderId===order.id && updatingStatus==='declined'}
                                          className="rounded-2xl border border-slate-200 bg-white px-8 py-4 text-xs font-black uppercase tracking-widest text-slate-600"
                                        >
                                          Decline
                                        </button>
                                      </>
                                    )}

                                    {order.status==='accepted' && (
                                      <button
                                        onClick={(event)=>handleStatusUpdate(event,order.id,'preparing')}
                                        disabled={!!updatingOrderId}
                                        aria-busy={updatingOrderId===order.id && updatingStatus==='preparing'}
                                        className="rounded-2xl bg-purple-600 px-8 py-4 text-xs font-black uppercase tracking-widest text-white"
                                      >
                                        Start Baking
                                      </button>
                                    )}

                                    {order.status==='preparing' && (
                                      <button
                                        onClick={(event)=>handleStatusUpdate(event,order.id,'ready')}
                                        disabled={!!updatingOrderId}
                                        aria-busy={updatingOrderId===order.id && updatingStatus==='ready'}
                                        className="rounded-2xl bg-indigo-500 px-8 py-4 text-xs font-black uppercase tracking-widest text-white"
                                      >
                                        Ready for Handoff
                                      </button>
                                    )}

                                    <button
                                      onClick={(event)=>{
                                        event.stopPropagation();
                                        navigate(`/chat/${order.buyer_id}`);
                                      }}
                                      className="rounded-2xl bg-slate-900 px-8 py-4 text-xs font-black uppercase tracking-widest text-white"
                                    >
                                      <SafeIcon
                                        icon={FiMessageCircle}
                                        className="mr-2 inline text-indigo-400"
                                      />
                                      Chat with Buyer
                                    </button>
                                  </div>
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      );
                    })}
                  </SellerOrderPagination>
                ) : (
                  <div className="rounded-[3rem] border border-dashed border-slate-200 bg-slate-50/50 py-40 text-center">
                    <SafeIcon
                      icon={FiShoppingBag}
                      className="mx-auto mb-8 text-6xl text-slate-200"
                    />
                    <h3 className="mb-4 text-3xl font-black text-slate-900">
                      No orders found
                    </h3>
                    <p className="text-lg font-medium tracking-wide text-slate-500">
                      New community orders will automatically appear here.
                    </p>
                  </div>
                )}
              </div>
            )}

            {activeTab==='calendar' && (
              <SellerOrdersCalendar
                orders={sellerOrders}
                onSelectOrder={handleCalendarOrderSelect}
              />
            )}

            {activeTab==='profile' && (
              <SellerProfileTab seller={currentSeller} userId={user?.id} />
            )}

            {activeTab==='plans' && currentSeller && (
              <SellerMembershipPlanTab seller={currentSeller} />
            )}

            {activeTab==='offerings' && (
              <OfferingsTab
                seller={currentSeller}
                onNavigateToProfile={()=>setActiveTab('profile')}
              />
            )}

            {activeTab==='payments' && (
              <PaymentsTab seller={currentSeller} />
            )}

            {activeTab==='finance' && (
              <SellerFinanceTab
                seller={currentSeller}
                orders={sellerOrders}
              />
            )}

            {activeTab==='tax' && hasRegisteredTax && (
              <SellerTaxTab seller={currentSeller} />
            )}

            {activeTab==='policies' && (
              <PolicyBuilderWizard seller={currentSeller} />
            )}
          </div>
        </div>
      </div>

      {orderToPrint && (
        <InvoiceDocument
          order={orderToPrint}
          onClose={()=>setOrderToPrint(null)}
        />
      )}
    </div>
  );
};

export default SellerDashboardPage;
